import React, { useEffect, useMemo, useState } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'
import { Dropdown } from 'primereact/dropdown'
import { Paginator } from 'primereact/paginator'
import { Toolbar } from 'primereact/toolbar'
import CrfPreview from './CrfPreview'

function Crfmain() {
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [records, setRecords] = useState({})
  const [totalRecords, setTotalRecords] = useState(0)
  const [showPreview, setShowPreview] = useState(false)
  const [selected, setSelected] = useState({ orderid: '', file: '', json: '' })
  const [isboverified, setIsboverified] = useState(null)

  const rows = useMemo(() => {
    if (!records || typeof records !== 'object') return []
    return Object.values(records)
  }, [records])

  const fetchData = async (page, size, isboverifiedParam = null) => {
    setIsLoading(true)
    setErrorMessage('')
    try {
      let url = `${process.env.REACT_APP_CRF}get-unverified-crf/?page_number=${page}&page_size=${size}`
      if (isboverifiedParam !== null && isboverifiedParam !== '') {
        url += `&isboverified=${isboverifiedParam}`
      }
      const res = await fetch(url)
      if (!res || !res.ok) {
        setErrorMessage('Failed to load data')
        return
      }
      const data = await res.json()
      if (data && data.result) {
        setRecords(data.result.records || {})
        const r = data.result
        const total = r.total_records || r.total || r.count || r.totalCount || 0
        if (typeof total === 'number') setTotalRecords(total)
      } else {
        setErrorMessage('No data found')
      }
    } catch (e) {
      setErrorMessage('Something went wrong while fetching data')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData(pageNumber, pageSize, isboverified)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, isboverified])

  const openPreview = (row) => {
    setSelected({ orderid: row.orderid, file: row.file, json: row.json })
    setShowPreview(true)
  }

  const handleSaved = () => {
    // Refresh current page after save
    fetchData(pageNumber, pageSize, isboverified)
  }

  const pageSizeOptions = [5, 10, 20, 50].map((n) => ({ label: n, value: n }))

  const statusFilterOptions = [
    { label: 'All', value: null },
    { label: 'Pending ', value: '0' },
    { label: 'Accepted', value: '1' },
    { label: 'Expired', value: '2' },
  ]

  const leftToolbarTemplate = (
    <div className="flex align-items-center gap-2">
      <h3 style={{ margin: 0 }}>Unverified CRF</h3>
    </div>
  )

  const rightToolbarTemplate = (
    <div className="flex align-items-center gap-2">
      <Dropdown
        value={isboverified}
        options={statusFilterOptions}
        onChange={(e) => {
          setIsboverified(e.value)
          setPageNumber(1) // Reset to first page when filter changes
        }}
        placeholder="Filter by Status"
        style={{ width: 180 }}
        disabled={isLoading}
      />
      <Button icon="pi pi-refresh" label="Refresh" className="p-button-text" onClick={() => fetchData(pageNumber, pageSize, isboverified)} disabled={isLoading} />
    </div>
  )

  return (
    <div style={{ padding: 16 }}>
      <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} style={{ marginBottom: 12 }} />

      {/* <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Dropdown value={pageSize} options={pageSizeOptions} onChange={(e) => setPageSize(e.value)} disabled={isLoading} style={{ width: 140 }} placeholder="Rows" />
      </div> */}

      {errorMessage ? (
        <div style={{ color: 'red', marginBottom: 12 }}>{errorMessage}</div>
      ) : null}

      <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
        <DataTable value={rows} loading={isLoading} emptyMessage="No data" responsiveLayout="scroll" showGridlines stripedRows size="small" rowHover>
          <Column field="orderid" header="Order ID" sortable style={{ minWidth: 180 }}></Column>
          <Column field="created_date" header="Created" style={{ minWidth: 200 }} body={(row) => row.created_date ? new Date(row.created_date).toLocaleString() : '-'}></Column>
          <Column field="isboverified" header="Status" style={{ minWidth: 140 }} body={(row) => {
            const status = row.isboverified
            if (status === '1') {
              return <Tag value="Accepted" severity="success" rounded />
            } else if (status === '2') {
              return <Tag value="Expired" severity="danger" rounded />
            } else {
              return <Tag value="Pending" severity="warning" rounded />
            }
          }}></Column>
          <Column
            header="File"
            style={{ minWidth: 140 }}
            body={(row) => row.file ? (
              <Button icon="pi pi-external-link" label="Open" className="p-button-text p-button-sm" onClick={() => window.open(row.file, '_blank', 'noopener,noreferrer')} />
            ) : ('-')}
          ></Column>
          <Column
            header="Action"
            style={{ minWidth: 160 }}
            body={(row) => (
              <div className="flex gap-2">
                {row.isboverified === '1' ? (
                  <Tag value="Accepted" severity="success" rounded />
                ) : (
                  <Button icon="pi pi-eye" label="Preview" className="p-button-sm" onClick={() => openPreview(row)} disabled={!row.file && !row.json} />
                )}
              </div>
            )}
          ></Column>
        </DataTable>
      </div>

      <div style={{ marginTop: 10 }}>
        <Paginator
          first={(pageNumber - 1) * pageSize}
          rows={pageSize}
          totalRecords={totalRecords || (pageNumber * pageSize + (rows.length === pageSize ? pageSize : rows.length))}
          onPageChange={(e) => {
            setPageNumber(e.page + 1)
            if (e.rows !== pageSize) setPageSize(e.rows)
          }}
          rowsPerPageOptions={[5, 10, 20, 50]}
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          disabled={isLoading}
        />
      </div>

      <CrfPreview
        visible={showPreview}
        onHide={() => setShowPreview(false)}
        fileUrl={selected.file}
        jsonString={selected.json}
        orderId={selected.orderid}
        onSave={handleSaved}
      />
    </div>
  )
}

export default Crfmain